import { Controller } from "@hotwired/stimulus";

// A small popup menu opened by one or more trigger buttons (aria-haspopup,
// aria-expanded). Closes on Escape (focus returns to the trigger), on a click
// or focus outside, and when an item is chosen. Arrow keys move between items.
export default class extends Controller {
  static targets = ["button", "panel"];

  connect() {
    this.boundOutside = this.closeOnOutside.bind(this);
    document.addEventListener("click", this.boundOutside);
    document.addEventListener("focusin", this.boundOutside);
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutside);
    document.removeEventListener("focusin", this.boundOutside);
  }

  toggle(event) {
    this.trigger = event.currentTarget;
    this.panelTarget.hidden ? this.open() : this.close();
  }

  open() {
    this.panelTarget.hidden = false;
    this.setExpanded(true);
    this.items[0]?.focus();
  }

  close({ refocus = false } = {}) {
    if (this.panelTarget.hidden) return;

    this.panelTarget.hidden = true;
    this.setExpanded(false);
    if (refocus) (this.trigger || this.buttonTargets[0]).focus();
  }

  choose() {
    this.close();
  }

  keydown(event) {
    if (this.panelTarget.hidden) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.close({ refocus: true });
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const items = this.items;
      const index = items.indexOf(document.activeElement);
      const step = event.key === "ArrowDown" ? 1 : -1;
      items[(index + step + items.length) % items.length]?.focus();
    }
  }

  closeOnOutside(event) {
    // iOS Safari doesn't focus a tapped link: focus goes to the nearest
    // focusable ancestor (main#main-content), before the click. That isn't
    // "outside", and closing then would swallow the tap on the menu item.
    if (event.type === "focusin" && event.target.contains(this.element)) return;
    if (!this.element.contains(event.target)) this.close();
  }

  setExpanded(expanded) {
    this.buttonTargets.forEach((button) =>
      button.setAttribute("aria-expanded", String(expanded))
    );
  }

  get items() {
    return Array.from(this.panelTarget.querySelectorAll("[role='menuitem']"));
  }
}
